import { useState } from 'react';
import {
  Plus, ChevronRight, CheckCircle2, XCircle, Clock,
  Send, Power, Pencil, Trash2, Copy, Check as CheckIcon,
} from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { RequireRole } from '@/components/auth/RequireRole';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  useWebhooks, useWebhookDeliveries, useCreateWebhook,
  useUpdateWebhook, useDeleteWebhook, useTestWebhook,
} from '@/api/hooks/useSettings';
import { cn } from '@/lib/utils';
import type { WebhookEndpoint } from '@/api/types/settings.types';

// ── Constants ─────────────────────────────────────────────────────────────────

const EVENT_TYPES = [
  'assessment.issued',
  'assessment.submitted',
  'assessment.approved',
  'assessment.sent_back',
  'risk_acceptance.pending',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function truncateUrlMiddle(url: string, maxLen = 58): string {
  if (url.length <= maxLen) return url;
  const half = Math.floor((maxLen - 3) / 2);
  return url.slice(0, half) + '…' + url.slice(url.length - half);
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy(e: React.MouseEvent) {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }
  return (
    <button
      onClick={handleCopy}
      className="opacity-0 group-hover/url:opacity-100 transition-opacity ml-1 text-text-secondary hover:text-text-primary"
      title="Copy URL"
    >
      {copied
        ? <CheckIcon className="h-3 w-3 text-accent-emerald" />
        : <Copy className="h-3 w-3" />
      }
    </button>
  );
}

// ── Delivery Panel ────────────────────────────────────────────────────────────

function DeliveryPanel({ webhookId, onClose }: { webhookId: string; onClose: () => void }) {
  const { data: deliveries, isLoading } = useWebhookDeliveries(webhookId);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-primary">Delivery History</h3>
        <button onClick={onClose} className="text-xs text-text-secondary hover:text-text-primary">Close</button>
      </div>
      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : (deliveries ?? []).length === 0 ? (
        <p className="text-xs text-text-secondary py-4 text-center">No deliveries yet</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto">
          {(deliveries ?? []).map((d) => {
            const isFailure = d.status !== 'success';
            return (
              <div
                key={d.id}
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-2.5',
                  isFailure
                    ? 'border-accent-rose/30 bg-accent-rose/5'
                    : 'border-border-subtle bg-bg-surface',
                )}
              >
                {d.status === 'success'
                  ? <CheckCircle2 className="h-4 w-4 text-accent-emerald shrink-0" />
                  : d.status === 'failed'
                  ? <XCircle className="h-4 w-4 text-accent-rose shrink-0" />
                  : <Clock className="h-4 w-4 text-accent-amber shrink-0" />
                }
                <div className="flex-1 min-w-0">
                  <p className={cn('text-xs font-mono', isFailure ? 'text-accent-rose' : 'text-text-primary')}>
                    {d.event_type}
                    {d.response_code && (
                      <span className={cn('ml-2', isFailure ? 'text-accent-rose' : 'text-text-secondary')}>
                        · HTTP {d.response_code}
                      </span>
                    )}
                  </p>
                  <p className="text-[10px] text-text-secondary">
                    {d.duration_ms && `${d.duration_ms}ms · `}
                    {formatDistanceToNow(parseISO(d.delivered_at), { addSuffix: true })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Webhook Modal (create + edit) ─────────────────────────────────────────────

interface WebhookModalProps {
  mode: 'create' | 'edit';
  initialValues?: Pick<WebhookEndpoint, 'id' | 'name' | 'url' | 'event_types'>;
  onClose: () => void;
}

function WebhookModal({ mode, initialValues, onClose }: WebhookModalProps) {
  const createMutation = useCreateWebhook();
  const updateMutation = useUpdateWebhook();
  const testMutation = useTestWebhook();
  const [justCreated, setJustCreated] = useState<WebhookEndpoint | null>(null);

  const [name, setName] = useState(initialValues?.name ?? '');
  const [url, setUrl] = useState(initialValues?.url ?? '');
  const [secret, setSecret] = useState('');
  const [eventTypes, setEventTypes] = useState<string[]>(initialValues?.event_types ?? []);

  function toggleEvent(e: string) {
    setEventTypes((prev) => prev.includes(e) ? prev.filter((x) => x !== e) : [...prev, e]);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!url.startsWith('https://')) { toast.error('URL must start with https://'); return; }
    if (eventTypes.length === 0) { toast.error('Select at least one event type'); return; }

    if (mode === 'edit' && initialValues) {
      updateMutation.mutate(
        { id: initialValues.id, name, url, event_types: eventTypes, ...(secret ? { secret } : {}) },
        {
          onSuccess: () => { toast.success('Webhook updated'); onClose(); },
          onError: () => toast.error('Failed to update webhook'),
        },
      );
    } else {
      createMutation.mutate(
        { name, url, eventTypes, secret: secret || undefined },
        {
          onSuccess: (wh) => { toast.success('Webhook created'); setJustCreated(wh); },
          onError: () => toast.error('Failed to create webhook'),
        },
      );
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Post-create success screen (create mode only)
  if (justCreated) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Webhook Created</DialogTitle>
            <DialogDescription className="sr-only">Webhook successfully created</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-text-secondary">
              <strong className="text-text-primary">{justCreated.name}</strong> is now active.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => testMutation.mutate(justCreated.id, {
                onSuccess: () => toast.success('Test event sent'),
                onError: () => toast.error('Test failed'),
              })}
              disabled={testMutation.isPending}
            >
              Send Test Event
            </Button>
            <Button className="w-full" onClick={onClose}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Webhook' : 'Create Webhook'}</DialogTitle>
          <DialogDescription className="sr-only">
            {mode === 'edit' ? 'Update webhook settings' : 'Configure a new webhook endpoint'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label>Name <span className="text-accent-rose">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Slack Alerts" required />
          </div>
          <div className="space-y-1.5">
            <Label>URL <span className="text-accent-rose">*</span></Label>
            <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.example.com/..." required />
          </div>
          <div className="space-y-1.5">
            <Label>{mode === 'edit' ? 'New Secret (leave blank to keep existing)' : 'Secret (optional)'}</Label>
            <Input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={mode === 'edit' ? '••••••••' : 'HMAC signing secret'}
            />
          </div>
          <div className="space-y-2">
            <Label>Event Types <span className="text-accent-rose">*</span></Label>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {EVENT_TYPES.map((et) => (
                <label key={et} className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer hover:text-text-primary">
                  <input type="checkbox" checked={eventTypes.includes(et)} onChange={() => toggleEvent(et)} className="rounded" />
                  <span className="font-mono text-xs">{et}</span>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter className="pt-2 border-t border-border-subtle">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isPending || !name || !url}>
              {mode === 'edit' ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Confirmation Dialog ─────────────────────────────────────────────────

function DeleteConfirmDialog({ webhook, onClose }: { webhook: WebhookEndpoint; onClose: () => void }) {
  const deleteMutation = useDeleteWebhook();

  function handleDelete() {
    deleteMutation.mutate(webhook.id, {
      onSuccess: () => { toast.success(`Webhook "${webhook.name}" deleted`); onClose(); },
      onError: () => toast.error('Failed to delete webhook'),
    });
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete webhook?</DialogTitle>
          <DialogDescription className="text-sm text-text-secondary mt-1">
            Delete <strong className="text-text-primary">"{webhook.name}"</strong>? Delivered events will remain
            in history but no new events will be sent to this URL.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Webhook Card ──────────────────────────────────────────────────────────────

function WebhookCard({ wh }: { wh: WebhookEndpoint }) {
  const updateMutation = useUpdateWebhook();
  const testMutation = useTestWebhook();
  const [expanded, setExpanded] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  function handleToggleActive(e: React.MouseEvent) {
    e.stopPropagation();
    updateMutation.mutate(
      { id: wh.id, is_active: !wh.is_active },
      {
        onSuccess: () => toast.success(wh.is_active ? 'Webhook disabled' : 'Webhook enabled'),
        onError: () => toast.error('Failed to update webhook'),
      },
    );
  }

  function handleTest(e: React.MouseEvent) {
    e.stopPropagation();
    testMutation.mutate(wh.id, {
      onSuccess: () => toast.success('Test delivery sent'),
      onError: (err: any) => toast.error(err?.response?.data?.errors?.[0]?.message ?? 'Test delivery failed'),
    });
  }

  return (
    <>
      <div className={cn('transition-opacity', !wh.is_active && 'opacity-70')}>
        {/* Card header row */}
        <div
          className="flex items-center gap-4 p-4 hover:bg-bg-surface-2 cursor-pointer"
          onClick={() => setExpanded((v) => !v)}
        >
          {/* Name + URL */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-text-primary">{wh.name}</p>
            <div className="flex items-center group/url">
              <p
                className="text-xs text-text-secondary font-mono truncate max-w-[420px]"
                title={wh.url}
              >
                {truncateUrlMiddle(wh.url)}
              </p>
              <CopyButton text={wh.url} />
            </div>
          </div>

          {/* Right-side controls */}
          <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
            {/* Event types badge — click to expand chip list */}
            <button
              onClick={(e) => { e.stopPropagation(); setShowEvents((v) => !v); }}
              className="focus:outline-none"
            >
              <Badge variant="muted" className="text-[10px] cursor-pointer hover:bg-bg-surface-3">
                {wh.event_types.length} event{wh.event_types.length !== 1 ? 's' : ''}
              </Badge>
            </button>

            {/* Last delivery status dot */}
            {wh.last_delivery_status === 'success' && <CheckCircle2 className="h-4 w-4 text-accent-emerald" />}
            {wh.last_delivery_status === 'failed' && <XCircle className="h-4 w-4 text-accent-rose" />}

            {/* Active/Inactive pill */}
            <Badge variant={wh.is_active ? 'emerald' : 'muted'} className="text-[10px]">
              {wh.is_active ? 'Active' : 'Inactive'}
            </Badge>

            {/* Action buttons */}
            <div className="flex items-center gap-0.5 ml-1">
              <button
                onClick={handleTest}
                disabled={testMutation.isPending}
                className="p-1.5 rounded hover:bg-bg-surface-3 text-text-secondary hover:text-accent-blue transition-colors disabled:opacity-40"
                title="Send test event"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={handleToggleActive}
                disabled={updateMutation.isPending}
                className={cn(
                  'p-1.5 rounded hover:bg-bg-surface-3 transition-colors disabled:opacity-40',
                  wh.is_active ? 'text-text-secondary hover:text-accent-amber' : 'text-accent-amber hover:text-accent-emerald',
                )}
                title={wh.is_active ? 'Disable webhook' : 'Enable webhook'}
              >
                <Power className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setEditOpen(true)}
                className="p-1.5 rounded hover:bg-bg-surface-3 text-text-secondary hover:text-text-primary transition-colors"
                title="Edit webhook"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => setDeleteOpen(true)}
                className="p-1.5 rounded hover:bg-bg-surface-3 text-text-secondary hover:text-accent-rose transition-colors"
                title="Delete webhook"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <ChevronRight
            className={cn('h-4 w-4 text-text-secondary transition-transform shrink-0', expanded && 'rotate-90')}
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          />
        </div>

        {/* Subscribed event type chips */}
        {showEvents && (
          <div className="px-4 pb-3 flex flex-wrap gap-1.5 border-t border-border-subtle bg-bg-surface-2 pt-3">
            {wh.event_types.map((et) => (
              <span key={et} className="inline-flex items-center px-2 py-0.5 rounded-full bg-bg-surface-3 border border-border-subtle text-[10px] font-mono text-text-secondary">
                {et}
              </span>
            ))}
          </div>
        )}

        {/* Delivery history panel */}
        {expanded && (
          <div className="px-4 pb-4 border-t border-border-subtle bg-bg-surface-2">
            <DeliveryPanel webhookId={wh.id} onClose={() => setExpanded(false)} />
          </div>
        )}
      </div>

      {editOpen && (
        <WebhookModal
          mode="edit"
          initialValues={{ id: wh.id, name: wh.name, url: wh.url, event_types: wh.event_types }}
          onClose={() => setEditOpen(false)}
        />
      )}
      {deleteOpen && (
        <DeleteConfirmDialog webhook={wh} onClose={() => setDeleteOpen(false)} />
      )}
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function WebhooksPage() {
  return (
    <RequireRole roles={['risk_manager', 'super_admin']} label="Webhooks">
      <WebhooksPageBody />
    </RequireRole>
  );
}

function WebhooksPageBody() {
  const { data: webhooks, isLoading } = useWebhooks();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Webhooks</h1>
          <p className="mt-1 text-sm text-text-secondary">Receive events at your endpoints</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> Create Webhook
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : (webhooks ?? []).length === 0 ? (
            <div className="py-16 px-8 text-center">
              <p className="text-sm font-medium text-text-primary mb-1">No webhooks configured yet</p>
              <p className="text-sm text-text-secondary mb-6">
                Create your first webhook to start receiving event notifications.
              </p>
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1.5" /> Create Webhook
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-border-subtle">
              {(webhooks ?? []).map((wh) => (
                <WebhookCard key={wh.id} wh={wh} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {createOpen && <WebhookModal mode="create" onClose={() => setCreateOpen(false)} />}
    </div>
  );
}
